import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { ImageComparison } from '@/components/ui/ImageComparison';
import { FlipWords } from '@/components/ui/flip-words';
import { BackgroundBeams } from '@/components/ui/background-beams';
import { urlFor } from '@/sanity/client';
import type { HeroSectionData, ContactInfoData } from '@/sanity/types';

// R2 - Carousel items for before/after comparisons
interface CarouselItem {
  before: string;
  after: string;
  alt: string;
}

const CAROUSEL_ITEMS: CarouselItem[] = [
  {
    before: 'https://res.cloudinary.com/dphpfdsk3/image/upload/v1764269115/modulo_home_old_rxcndr.jpg',
    after: 'https://res.cloudinary.com/dphpfdsk3/image/upload/v1764269542/modulo_home_new_hlbrx7.jpg',
    alt: 'Módulo prefabricado',
  },
  {
    before: 'https://res.cloudinary.com/dphpfdsk3/image/upload/v1764657075/modulo_home_old_2_p63nam.jpg',
    after: 'https://res.cloudinary.com/dphpfdsk3/image/upload/v1764657075/modulo_home_new_2_p1pzwl.jpg',
    alt: 'Interior módulo prefabricado',
  },
  {
    before: 'https://res.cloudinary.com/dphpfdsk3/image/upload/v1764657645/modulo_home_old_3_kh4rje.jpg',
    after: 'https://res.cloudinary.com/dphpfdsk3/image/upload/v1764657345/modulo_home_new_3_idsxrd.jpg',
    alt: 'Suelo módulo prefabricado',
  },
];

const WHATSAPP_URL = 'https://wa.me/?text=Hola%20INBOCA%2C%20me%20gustaría%20consultar%20sobre%20sus%20servicios';

interface HeroSectionProps {
  data?: HeroSectionData | undefined;
  contactInfo?: ContactInfoData | undefined;
}

/**
 * R2 - Hero section with carousel of before/after comparisons
 * R4 - Accepts optional Sanity data, falls back to hardcoded defaults
 */
export function HeroSection({ data, contactInfo }: HeroSectionProps) {
  const badge = data?.badge ?? 'Bienvenido a INBOCA';
  const headingPrefix = data?.headingPrefix ?? 'Creamos espacios';
  const flipWords = data?.flipWords ?? ['Inspiradores', 'Funcionales', 'Sostenibles', 'Únicos'];
  const description = data?.description ?? 'INBOCA es tu socio corporativo en soluciones de diseño y mobiliario. Con más de una década de experiencia, transformamos tu visión en realidad.';
  const ctaPrimary = data?.ctaPrimaryText ?? 'Ver Productos';
  const ctaSecondary = data?.ctaSecondaryText ?? 'Contactar';

  // R4 - Resolve carousel: Sanity images via urlFor(), or fallback Cloudinary URLs
  const resolvedItems = data?.carouselItems?.length
    ? data.carouselItems.map(item => ({
        before: urlFor(item.beforeImage).width(800).url(),
        after: urlFor(item.afterImage).width(800).url(),
        alt: item.alt,
      }))
    : CAROUSEL_ITEMS;

  const whatsappUrl = contactInfo
    ? `https://wa.me/${contactInfo.whatsappNumber}?text=${encodeURIComponent(contactInfo.whatsappDefaultMessage)}`
    : WHATSAPP_URL;
  const [currentIndex, setCurrentIndex] = useState(0);

  // R3 - Navigation handlers with infinite loop
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => 
      prev === 0 ? resolvedItems.length - 1 : prev - 1
    );
  }, [resolvedItems.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => 
      prev === resolvedItems.length - 1 ? 0 : prev + 1
    );
  }, [resolvedItems.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // R3 - Safe access (array is always populated, assertion is safe)
  const currentItem = resolvedItems[currentIndex]!;

  return (
    <section className="py-14 sm:py-16 bg-slate-50 relative overflow-hidden">
      {/* Animated Background */}
      <BackgroundBeams />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="animate-fade-in-up">
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-inboca-teal/10 rounded-full text-inboca-teal font-semibold text-sm mb-6">
                {badge}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {headingPrefix}
                <FlipWords
                  words={flipWords}
                  duration={3000}
                  className="text-inboca-teal"
                />
              </h1>
              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed mb-8">
                {description}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/productos"
                className="inline-flex items-center justify-center gap-2 bg-inboca-teal hover:bg-inboca-teal/90 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {ctaPrimary}
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-inboca-orange hover:bg-inboca-orange/90 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <FaWhatsapp className="h-5 w-5" />
                {ctaSecondary}
              </a>
            </div>
          </div>

          {/* Right: Before/After Carousel */}
          <div className="relative z-15 animate-fade-in-up delay-200">
            <div className="relative h-96 sm:h-[500px] rounded-2xl overflow-hidden shadow-2xl group">
              {/* ImageComparison for current slide */}
              <ImageComparison
                itemLeft={
                  <div className="w-full h-full relative">
                    <img
                      src={currentItem.before}
                      alt={`${currentItem.alt} - Antes`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                }
                itemRight={
                  <div className="w-full h-full relative">
                    <img
                      src={currentItem.after}
                      alt={`${currentItem.alt} - Después`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                }
                labelLeft="Antes"
                labelRight="Después"
              />

              {/* Navigation Arrows */}
              <button
                onClick={goToPrevious}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm transition-all duration-300 opacity-0 group-hover:opacity-100"
                aria-label="Slide anterior"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm transition-all duration-300 opacity-0 group-hover:opacity-100"
                aria-label="Siguiente slide"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Dot Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {resolvedItems.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      index === currentIndex
                        ? 'bg-white scale-125'
                        : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Ir al slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
